import React, { useState, Fragment } from "react";
import * as PDFJS from "pdfjs-dist/legacy/build/pdf";
import styled, { keyframes } from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import SearchBar from "../../common/CustomTable/common/SearchBar";
import { Modal } from "@material-ui/core";
import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.entry";
import useLoader from "../../../hooks/useLoader";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";

PDFJS.GlobalWorkerOptions.isEvalSupported = false;

PDFJS.GlobalWorkerOptions.workerSrc = workerSrc;

const PdfViewer = ({ urls = [], bundleCode }) => {
  const [modal, setModal] = useState(false);
  const [inputText, setInputText] = useState("");
  const [results, setResults] = useState([]);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const handleSearch = async () => {
    if (!urls.length) {
      enqueueSnackbar("PDF URLs missing", { variant: "error" });
      return;
    }

    if (!inputText.trim()) return;

    let found = [];

    try {
      setLoader({ state: true, message: "Searching PDFs..." });

      for (let fileIndex = 0; fileIndex < urls.length; fileIndex++) {
        const pdfUrl = urls[fileIndex];

        const response = await fetch(pdfUrl);
        if (!response.ok) continue;

        const buffer = await response.arrayBuffer();
        const pdf = await PDFJS.getDocument({ data: buffer }).promise;

        for (let pageIndex = 1; pageIndex <= pdf.numPages; pageIndex++) {
          const pageObj = await pdf.getPage(pageIndex);
          const textContent = await pageObj.getTextContent();
          const text = textContent.items
            .map((item) => item.str)
            .join(" ")
            .toLowerCase();

          if (text.includes(inputText.toLowerCase())) {
            found.push({
              url: pdfUrl,
              page: pageIndex,
            });
          }
        }
      }

      if (found.length) {
        setResults(found);
        setModal(true);
      } else {
        enqueueSnackbar("Search keyword not found", { variant: "info" });
      }
    } catch (err) {
      const message = getErrorMessage(err);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <Fragment>
      <SearchContainer>
        <SearchBar
          placeHolderText="Search keyword"
          backgroundColor="#ffffff"
          {...{ inputText, setInputText }}
        />
        {inputText?.length ? (
          <SearchBtn onClick={handleSearch}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            Search
          </SearchBtn>
        ) : null}
      </SearchContainer>

      <Modal open={modal} onClose={() => setModal(false)}>
        <Overlay>
          <ResultsCard>
            <ResultsHeader>
              <ResultsIconCircle>
                <span style={{ fontSize: 20 }}>{"🔍"}</span>
              </ResultsIconCircle>
              <ResultsHeaderText>
                <ResultsTitle>Search Results</ResultsTitle>
                <ResultsSubtitle>
                  {results?.length
                    ? `Found on ${results.length} page${results.length > 1 ? "s" : ""}`
                    : "Searching..."}
                </ResultsSubtitle>
              </ResultsHeaderText>
              <CloseBtn onClick={() => setModal(false)}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ResultsHeader>
            <ResultsDivider />

            <ResultsKeyword>
              <KeywordLabel>Keyword</KeywordLabel>
              <KeywordChip>"{inputText}"</KeywordChip>
            </ResultsKeyword>

            <ResultsList>
              {results.map((r, i) => (
                <ResultItem
                  key={i}
                  onClick={() => window.open(`${r.url}#page=${r.page}`)}
                >
                  <ResultLeft>
                    <PageBadge>pg {r.page}</PageBadge>
                    <ResultLink>
                      {bundleCode} — Page {r.page}
                    </ResultLink>
                  </ResultLeft>
                  <OpenIcon>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </OpenIcon>
                </ResultItem>
              ))}
            </ResultsList>
          </ResultsCard>
        </Overlay>
      </Modal>
    </Fragment>
  );
};

export default PdfViewer;

/* ── Styles ── */

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 15px;

  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const SearchBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  height: 36px;
  background: transparent;
  border: 1.5px solid #00838c;
  border-radius: 8px;
  color: #00838c;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  &:hover {
    background: #00838c;
    color: #fff;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
`;

const ResultsCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 380px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
  animation: ${fadeSlideIn} 0.25s ease;
  overflow: hidden;
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 22px 22px 18px;
`;

const ResultsIconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e6f7f8 0%, #b2e8eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ResultsHeaderText = styled.div`
  flex: 1;
`;

const ResultsTitle = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 17px;
  color: #0f172a;
`;

const ResultsSubtitle = styled.p`
  margin: 3px 0 0;
  font-size: 12px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
  }
`;

const ResultsDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 0 22px;
`;

const ResultsKeyword = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px 10px;
`;

const KeywordLabel = styled.span`
  font-size: 12px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const KeywordChip = styled.span`
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #00838c;
  background: #f0fdfb;
  border: 1px solid #99f6e4;
  padding: 3px 10px;
  border-radius: 100px;
`;

const ResultsList = styled.div`
  padding: 4px 22px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow-y: auto;
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: #00838c;
    background: #f0fdfb;
    transform: translateX(3px);
  }
`;

const ResultLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageBadge = styled.span`
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #fff;
  background: #00838c;
  padding: 2px 8px;
  border-radius: 6px;
  white-space: nowrap;
`;

const ResultLink = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #334155;
`;

const OpenIcon = styled.div`
  color: #94a3b8;
  display: flex;
  align-items: center;
`;

export const Label = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  line-height: 1.9;
  margin-top: 12px;
  color: ${COLORS.INPUT_LABEL};
  display: inline-block;
`;

export const HyperLink = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: #00838c;
  text-decoration: underline;
  cursor: pointer;
  display: inline-block;
`;
